import React, {createContext, useContext, useEffect, useRef, useState} from 'react'
import _ from 'lodash'
import {validate as validateEmail} from 'email-validator'
import {useLocation, useNavigate} from 'react-router-dom'
import {useGet, useMutate} from 'restful-react'
import {Button, Dimmer, Form, Input, Loader, Segment, Checkbox, Modal} from 'semantic-ui-react'
import {MessageContext} from '@/features/errors'
import logo from '@/logo.png'
import logoDark from '@/logo-dark.png'
import './style.css'
import {usePostHog} from 'posthog-js/react'

export type permissions =
  | 'ainar_chat.chat'
  | 'api_key.adbreak_api'
  | 'api_key.aggregated_keyword_api'
  | 'api_key.api_key.transcript_keywords_api'
  | 'api_key.api_key_permissions_admin'
  | 'api_key.binge_markers_api'
  | 'api_key.catalog_api'
  | 'api_key.catalog_api_config_admin'
  | 'api_key.content_summary_api'
  | 'api_key.contextual_adbreak_api'
  | 'api_key.contextual_segment_api'
  | 'api_key.emotion_api'
  | 'api_key.fingerprint_api'
  | 'api_key.fingerprint_plus_api'
  | 'api_key.gfp_api'
  | 'api_key.jitpreview_api'
  | 'api_key.jitthumbnail_api'
  | 'api_key.mood_api'
  | 'api_key.movie_chat_api'
  | 'api_key.nudity_api'
  | 'api_key.personal_recs_api'
  | 'api_key.preview_api'
  | 'api_key.preview_api_write'
  | 'api_key.profanity_api'
  | 'api_key.semantic_api'
  | 'api_key.similar_api'
  | 'api_key.synopsis_api'
  | 'api_key.task_api'
  | 'api_key.thumbnail_api'
  | 'api_key.thumbnail_api_write'
  | 'api_key.transcript_keywords_api'
  | 'auth_utils.access_to_all_catalogs'
  | 'auth_utils.access_to_compare_tool'
  | 'auth_utils.access_to_internal_tool'
  | 'auth_utils.add_configuration'
  | 'auth_utils.add_usercatalogmodel'
  | 'auth_utils.add_vionlabsuser'
  | 'auth_utils.change_configuration'
  | 'auth_utils.change_usercatalogmodel'
  | 'auth_utils.change_vionlabsuser'
  | 'auth_utils.delete_configuration'
  | 'auth_utils.delete_usercatalogmodel'
  | 'auth_utils.delete_vionlabsuser'
  | 'auth_utils.list_groups'
  | 'auth_utils.view_configuration'
  | 'auth_utils.view_usercatalogmodel'
  | 'auth_utils.view_vionlabsuser'
  | 'catalog_manager.admin_catalog_feature_code'
  | 'catalog_manager.change_catalog'
  | 'catalog_manager.create_available_task'
  | 'catalog_manager.create_catalog'
  | 'catalog_manager.create_feature_code'
  | 'catalog_manager.edit_feature_code'
  | 'catalog_manager.reset_catalog'
  | 'catalog_manager.reset_job'
  | 'catalog_manager.view_available_task'
  | 'catalog_manager.view_catalog'
  | 'catalog_manager.view_feature_code'
  | 'gfp_result.access_internal_catalog'
  | 'gfp_result.view'
  | 'label.add_label'
  | 'label.can_view_legacy_tools'
  | 'label.list_labels'
  | 'label.video_lab_access'
  | 'log_provider.view_logs'
  | 'operation_history.add_operationhistorymodel'
  | 'operation_history.change_operationhistorymodel'
  | 'operation_history.delete_operationhistorymodel'
  | 'operation_history.view_operationhistorymodel'
  | 'proc_admin.add_catalogresetconfigmodel'
  | 'proc_admin.change_catalogresetconfigmodel'
  | 'proc_admin.delete_catalogresetconfigmodel'
  | 'proc_admin.view_catalogresetconfigmodel'
  | 'report.analytics'
  | 'report_admin.add_catalogproducthistory'
  | 'report_admin.change_catalogproducthistory'
  | 'report_admin.delete_catalogproducthistory'
  | 'report_admin.view_catalogproducthistory'
  | 'thumbnail.access_internal_catalog'
  | 'upload.add_defaultproducts'
  | 'upload.add_uploadcountquota'
  | 'upload.add_userpreviewconfig'
  | 'upload.add_useruploadcatalogmodel'
  | 'upload.change_defaultproducts'
  | 'upload.change_uploadcountquota'
  | 'upload.change_userpreviewconfig'
  | 'upload.change_useruploadcatalogmodel'
  | 'upload.delete_defaultproducts'
  | 'upload.delete_uploadcountquota'
  | 'upload.delete_userpreviewconfig'
  | 'upload.delete_useruploadcatalogmodel'
  | 'upload.download'
  | 'upload.upload'
  | 'upload.view_defaultproducts'
  | 'upload.view_uploadcountquota'
  | 'upload.view_userpreviewconfig'
  | 'upload.view_useruploadcatalogmodel'
  | 'user.add_userconfiguration'
  | 'user.change_userconfiguration'
  | 'user.delete_userconfiguration'
  | 'user.user_admin'
  | 'user.view_userconfiguration'
  | 'report.customer_dashboard'

export interface IUserCapabilitiesResponse {
  default_catalog: string
  allowed_catalogs: string[]
  upload_catalogs: string[]
  permissions: permissions[]
  user: {
    username: string
    email: string
    first_name: string
    last_name: string
  }
}

export interface IAuthWallProps {
  children: React.ReactNode
}

export interface IAuthContext extends IUserCapabilitiesResponse {
  getCookies: () => any
  signOut: () => Promise<void>
  havePermission: (require: permissions | permissions[]) => boolean
  csrfToken: string
}

export interface IRequirePermissionProps {
  children: React.ReactNode
  isPresent?: boolean
  require: permissions | permissions[]
}

const eulaUrl = '/assets/Vionlabs%20United%20Portal%20Terms%20of%20Use.pdf'

const bgImages = [
  {
    text: 'Batman Returns 1992 - Copyright Warners Bros',
    img: new URL('./images/batman.gif', import.meta.url).href,
  },
  {
    text: 'Blade Runner 1982 - Copyright Warners Bros',
    img: new URL('./images/bladerunner.gif', import.meta.url).href,
  },
  {
    text: 'Game of Thrones - Copyright HBO',
    img: new URL('./images/got.gif', import.meta.url).href,
  },
  {
    text: 'Guardians of the Galaxy 2014 - Copyright Marvel Studios',
    img: new URL('./images/groot.gif', import.meta.url).href,
  },
  {
    text: 'Hero 2002 - Copyright Miramax',
    img: new URL('./images/hero.gif', import.meta.url).href,
  },
  {
    text: 'Dredd 2012 - Copyright DNA Films',
    img: new URL('./images/judgedredd.gif', import.meta.url).href,
  },
  {
    text: 'Kill Bill: Vol 1 2003 - Copyright Miramax ',
    img: new URL('./images/killbill.gif', import.meta.url).href,
  },
  {
    text: 'Lord of The Rings 2001 - Copyright New Line Cinema',
    img: new URL('./images/lotr.gif', import.meta.url).href,
  },
  {
    text: 'O Brother, Where Art Thou? 2000 - Copyright Universial Pictures ',
    img: new URL('./images/obrother.gif', import.meta.url).href,
  },
  {
    text: 'Old Boy 2003 - Copyright Show East',
    img: new URL('./images/oldboy.gif', import.meta.url).href,
  },
  {
    text: 'Simpsons - Copyright Fox',
    img: new URL('./images/simpsons.gif', import.meta.url).href,
  },
  {
    text: 'Sin City 2005 - Copyright Dimension Films  ',
    img: new URL('./images/sincity.gif', import.meta.url).href,
  },
  {
    text: 'Snowpiercer 2013 - Copyright Moho Film',
    img: new URL('./images/snowpiercer.gif', import.meta.url).href,
  },
]

export const AuthContext = createContext<IAuthContext>({
  default_catalog: '',
  allowed_catalogs: [],
  upload_catalogs: [],
  permissions: [],
  csrfToken: '',
  user: {
    username: '',
    email: '',
    first_name: '',
    last_name: '',
  },
  havePermission: () => false,
  getCookies: () => {},
  signOut: async () => {},
})

export function RequirePermission(props: IRequirePermissionProps) {
  const {children, isPresent, require} = {
    isPresent: true,
    ...props,
  }
  const {permissions} = useContext(AuthContext)
  if (_.isArray(require)) {
    if ((_.intersection(permissions, require).length === require.length) !== isPresent) return null
  } else {
    if (_.includes(permissions, require) !== isPresent) return null
  }
  if (!children) return null
  return children
}

export function AuthWall(props: IAuthWallProps) {
  const {children} = props
  const posthog = usePostHog()

  const {logInfo} = useContext(MessageContext)

  const location = useLocation()
  const navigate = useNavigate()

  const bgImage = useRef(_.sample(bgImages))

  const [activeSession, setActiveSession] = useState(getCookies().active_session)
  const [csrfToken, setCsrfToken] = useState(getCookies().csrftoken)
  const [userCapabilities, setUserCapabilities] = useState<IUserCapabilitiesResponse>()
  const [authFormState, setAuthFormState] = useState({
    username: '',
    password: '',
  })
  const [registerFormState, setRegisterFormState] = useState({
    password: '',
    password_repeat: '',
    eula_accepted: false,
  })
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false)

  const {mutate: sendAuthRequest, loading: isAuthenticating} = useMutate({
    path: '/api/auth/login/',
    verb: 'POST',
  })
  const {mutate: sendSignoutRequest} = useMutate({
    path: '/api/auth/logout/',
    verb: 'POST',
    requestOptions: () => ({
      headers: {
        'X-CSRFToken': getCookies().csrftoken,
      },
    }),
  })
  const {refetch: refetchPermissions} = useGet({
    lazy: true,
    path: '/api/auth/user_info/',
    resolve: (res: IUserCapabilitiesResponse) =>
      setUserCapabilities((prev) => {
        if (_.isEqual(res, prev)) return prev
        posthog.identify(res.user.email, res.user)
        return res
      }),
  })

  const {mutate: activateUser, loading: isActivateLoading} = useMutate({
    verb: 'POST',
    path: () => {
      const [_path, uid, token] = _.chain(location.pathname).trim('/').split('/').value()
      return `/api/user-manager/activate-invited-user/${uid}/${token}`
    },
    requestOptions: () => ({
      headers: {
        'X-CSRFToken': getCookies().csrftoken,
      },
    }),
  })

  const {mutate: requestResetPassword, loading: isRequestResetPasswordLoading} = useMutate({
    verb: 'POST',
    path: '/api/user-manager/request-reset-password',
    requestOptions: () => ({
      headers: {
        'X-CSRFToken': getCookies().csrftoken,
      },
    }),
  })

  const {mutate: confirmResetPassword, loading: isConfirmResetPasswordLoading} = useMutate({
    verb: 'POST',
    path: () => {
      const [_path, uid, token] = _.chain(location.pathname).trim('/').split('/').value()
      return `/api/user-manager/reset-password/${uid}/${token}`
    },
    requestOptions: () => ({
      headers: {
        'X-CSRFToken': getCookies().csrftoken,
      },
    }),
  })

  useEffect(() => {
    if (!activeSession) return
    if (!userCapabilities) refetchPermissions()

    const interval = setInterval(() => {
      try {
        refetchPermissions()
      } catch (_error) {
        clearInterval(interval)
      }
    }, 120000)

    return () => clearInterval(interval)
  }, [activeSession])

  function refreshCookieState() {
    const c = getCookies()
    if (activeSession !== c.active_session) setActiveSession(c.active_session)
    if (csrfToken !== c.csrftoken) setCsrfToken(c.csrftoken)
  }

  function getCookies() {
    return _.chain(document.cookie)
      .split(';')
      .map((c) => _.split(_.trim(c), '='))
      .fromPairs()
      .value()
  }

  function handleAuthFormChange(_e: any, {name, value}: {name: string; value: any}) {
    setAuthFormState((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  function handleRegisterFormChange(_e: any, {name, value}: {name: string; value: any}) {
    setRegisterFormState((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  async function authenticate() {
    try {
      await sendAuthRequest(authFormState)
    } catch (_error) {
      return
    }
    refreshCookieState()
  }

  async function signOut() {
    try {
      await sendSignoutRequest(undefined)
    } catch (error) {
      console.error(error)
      document.location.reload()
    } finally {
      refreshCookieState()
      setUserCapabilities(undefined)
    }
  }

  function havePermission(require: permissions | permissions[]): boolean {
    const userPerm = userCapabilities?.permissions
    if (_.isArray(require)) {
      if (_.intersection(userPerm, require).length === require.length) return true
    } else {
      if (_.includes(userPerm, require)) return true
    }
    return false
  }

  function isAuthFormEmailValid(): boolean {
    return validateEmail(authFormState['username'])
  }

  function isRegisterFormPasswordValid(): boolean {
    return (
      registerFormState['password'] !== undefined &&
      registerFormState['password'].length >= 5 &&
      registerFormState['password'] === registerFormState['password_repeat']
    )
  }

  function isRegisterFormDataValid() {
    return isRegisterFormPasswordValid() && registerFormState['eula_accepted'] === true
  }

  if (location.pathname.startsWith('/register/')) {
    return (
      <div className='auth-wall'>
        {bgImage.current ?
          <div
            className='background'
            style={{backgroundImage: `url(${bgImage.current.img})`}}
          />
        : null}
        <Form className='auth-form'>
          <div
            className='logo light'
            style={{backgroundImage: `url(${logo})`}}
          />
          <div
            className='logo dark'
            style={{backgroundImage: `url(${logoDark})`}}
          />
          <Form.Field
            control={Input}
            type='password'
            label='Password:'
            icon='key'
            name='password'
            required
            value={registerFormState['password']}
            onChange={handleRegisterFormChange}
          />
          <Form.Field
            control={Input}
            type='password'
            label='Repeat password:'
            icon='key'
            name='password_repeat'
            required
            value={registerFormState['password_repeat']}
            onChange={handleRegisterFormChange}
          />
          <Form.Field
            control={() => (
              <>
                <Checkbox
                  checked={registerFormState['eula_accepted']}
                  onChange={(_e, {checked}) => {
                    setRegisterFormState((prev) => ({
                      ...prev,
                      eula_accepted: checked || false,
                    }))
                  }}
                />
                <span className='eula-label'>
                  I have read and accept the{' '}
                  <a
                    href={eulaUrl}
                    target='_blank'
                  >
                    EULA
                  </a>
                </span>
              </>
            )}
            icon='password'
            name='eula_accepted'
            required
            value={registerFormState['eula_accepted']}
            onChange={handleRegisterFormChange}
          />
          <Button
            color='blue'
            icon='user'
            content='register'
            disabled={!isRegisterFormDataValid() || isActivateLoading}
            onClick={async () => {
              if (!isRegisterFormDataValid()) return
              await activateUser({
                password: registerFormState['password'],
              })
              navigate('/', {replace: true})
            }}
          />
          <Dimmer active={isActivateLoading}>
            <Loader />
          </Dimmer>
        </Form>
      </div>
    )
  }

  if (location.pathname.startsWith('/reset/')) {
    return (
      <div className='auth-wall'>
        {bgImage.current ?
          <div
            className='background'
            style={{backgroundImage: `url(${bgImage.current.img})`}}
          />
        : null}
        <Form className='auth-form'>
          <div
            className='logo light'
            style={{backgroundImage: `url(${logo})`}}
          />
          <div
            className='logo dark'
            style={{backgroundImage: `url(${logoDark})`}}
          />
          <Form.Field
            control={Input}
            type='password'
            label='Password:'
            icon='key'
            name='password'
            required
            value={registerFormState['password']}
            onChange={handleRegisterFormChange}
          />
          <Form.Field
            control={Input}
            type='password'
            label='Repeat password:'
            icon='key'
            name='password_repeat'
            required
            value={registerFormState['password_repeat']}
            onChange={handleRegisterFormChange}
          />
          <Button
            color='blue'
            icon='user'
            content='reset'
            disabled={!isRegisterFormPasswordValid() || isConfirmResetPasswordLoading}
            onClick={async () => {
              if (!isRegisterFormPasswordValid()) return
              await confirmResetPassword({
                password: registerFormState['password'],
              })
              logInfo('Your password have changed')
              navigate('/', {replace: true})
            }}
          />
          <Dimmer active={isConfirmResetPasswordLoading}>
            <Loader />
          </Dimmer>
        </Form>
      </div>
    )
  }

  const _dev = false // process.env.NODE_ENV === 'development';
  if (_dev) return <>{children}</>

  // User needs to sign in
  if (!activeSession) {
    return (
      <div className='auth-wall'>
        {bgImage.current ?
          <div
            className='background'
            style={{backgroundImage: `url(${bgImage.current?.img})`}}
          />
        : null}
        <div className='auth-form'>
          <Form onSubmit={authenticate}>
            <div
              className='logo light'
              style={{backgroundImage: `url(${logo})`}}
            />
            <div
              className='logo dark'
              style={{backgroundImage: `url(${logoDark})`}}
            />
            <Form.Field
              control={Input}
              type='text'
              label='Username:'
              icon='user'
              name='username'
              required
              value={authFormState['username']}
              onChange={handleAuthFormChange}
            />
            <Form.Field
              control={Input}
              type='password'
              label='Password:'
              name='password'
              icon='key'
              required
              value={authFormState['password']}
              onChange={handleAuthFormChange}
            />
            <Form.Field
              control={Button}
              icon='key'
              content='Sign-in'
              type='submit'
              fluid
              color='blue'
            />
          </Form>
          <Button
            fluid
            onClick={() => {
              window.location.href = '/api/auth/workos/authorize/'
            }}
            style={{marginTop: '0.5em'}}
          >
            <img
              src='https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg'
              alt='Google'
              style={{width: 18, height: 18, marginRight: 8, verticalAlign: 'middle'}}
            />
            Continue with Google
          </Button>
          <div
            style={{
              display: 'flex',
              justifyContent: 'right',
              fontSize: '0.9em',
              cursor: 'pointer',
            }}
          >
            <Modal
              open={isForgotPasswordOpen}
              onOpen={() => setIsForgotPasswordOpen(true)}
              onClose={() => setIsForgotPasswordOpen(false)}
              trigger={<div>Forgot password?</div>}
            >
              <Modal.Header>Reset password</Modal.Header>
              <Modal.Content>
                <Segment inverted>
                  <Form
                    onSubmit={async () => {
                      await requestResetPassword(
                        {
                          email: authFormState['username'],
                        },
                        {
                          queryParams: {
                            reset_password_path: 'reset',
                          },
                        }
                      )
                      logInfo('Please check your inbox for password reset link')
                      setIsForgotPasswordOpen(false)
                    }}
                  >
                    <Form.Field
                      control={Input}
                      type='text'
                      label='E-mail:'
                      icon='user'
                      name='username'
                      required
                      // TODO: non-shared state?
                      value={authFormState['username']}
                      onChange={handleAuthFormChange}
                    />
                    <Form.Field
                      control={Button}
                      icon='key'
                      content='Submit'
                      type='submit'
                      fluid
                      color='blue'
                      disabled={!isAuthFormEmailValid() || isRequestResetPasswordLoading}
                    />
                  </Form>
                </Segment>
              </Modal.Content>
            </Modal>
          </div>
          <Dimmer active={isAuthenticating}>
            <Loader />
          </Dimmer>
        </div>
      </div>
    )
  }

  if (!userCapabilities) {
    return null
  }
  if (!children) return null

  return (
    <AuthContext.Provider
      value={{
        ...userCapabilities,
        getCookies,
        signOut,
        havePermission,
        csrfToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
